import ClassNames from 'classnames'
import * as React from 'react'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { postService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import {
  IUser,
  IJobBulkPlan,
  IAgentRank,
  ITourSkill,
  ISoundOutTemplate,
  IFormalRequestTemplate,
  ITourMemoTemplate,
  ICategory,
  ITag,
  ILanguage,
  IArea,
  ICancelPolicy,
  IDisplayLanguage,
  ISkill,
} from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { getPostStatusKey } from '../../../../utils/post'
import { Button } from '../../../atoms'
import SidebarLayout from '../../_layouts/sidebarLayout'
import {
  Calendar,
  FlashMessage,
  PostAddress,
  PostBasicInfo,
  PostGroupTourPrice,
  PostImages,
  PostPrivateTourPrice,
  PostRemindMailTemplate,
  PostStartTime,
  PostVideos,
  PostGuideRankFee,
  PostDetailInfo,
  PostSettings,
  PostCost,
  JobBulkPlanList,
} from '../../../organisms'

interface IWindow {
  flashMessages: FlashMessage
  location: any
  onpopstate: any
  google: any
  innerWidth: number
}
declare var window: IWindow

const NAV_STATUS_LABELS: Record<string, string> = {
  draft: 'generic.draft',
  published: 'generic.published',
  limited_published: 'post.limited_access_published',
  suspended: 'generic.suspended',
  closed: 'post.status_closed',
}

const SELECTABLE_STATUS_KEYS: Record<string, string[]> = {
  draft: ['published', 'limited_published', 'closed'],
  published: ['limited_published', 'suspended', 'closed'],
  limited_published: ['published', 'suspended', 'closed'],
  suspended: ['published', 'limited_published', 'closed'],
  closed: ['published', 'limited_published', 'suspended'],
}

const NavStatusDropdown: React.FC<{
  post: any
  onPublish: () => void
  onLimitedPublish: () => void
  onSuspend: () => void
  onClose: () => void
  publishDisabled?: boolean
}> = ({ post, onPublish, onLimitedPublish, onSuspend, onClose, publishDisabled = false }) => {
  const [menuPos, setMenuPos] = React.useState<{ top: number; right: number } | null>(null)
  const stateKey = getPostStatusKey(post)
  const allOptions = [
    {
      key: 'published',
      label: I18n.t('post.status_action_publish'),
      handler: onPublish,
      disabled: publishDisabled,
    },
    {
      key: 'limited_published',
      label: I18n.t('post.status_action_limited_publish'),
      handler: onLimitedPublish,
      disabled: publishDisabled,
    },
    {
      key: 'suspended',
      label: I18n.t('post.status_action_suspend'),
      handler: onSuspend,
      disabled: false,
    },
    {
      key: 'closed',
      label: I18n.t('post.status_action_close'),
      handler: onClose,
      disabled: false,
    },
  ]
  const selectableKeys = SELECTABLE_STATUS_KEYS[stateKey] ?? []

  return (
    <div
      className={`NavStatusDropdown ${stateKey}`}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setMenuPos(null)
        }
      }}
    >
      <button
        className="NavStatusDropdown_btn"
        onClick={e => {
          const rect = e.currentTarget.getBoundingClientRect()
          setMenuPos(
            menuPos ? null : { top: rect.bottom + 4, right: window.innerWidth - rect.right }
          )
        }}
      >
        <span>{I18n.t(NAV_STATUS_LABELS[stateKey])}</span>
        <svg
          className="NavStatusDropdown_icon"
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          fill="currentColor"
          viewBox="0 0 24 24"
        >
          <path d="m7.41 8.295 4.59 4.58 4.59-4.58L18 9.705l-6 6-6-6 1.41-1.41Z" />
        </svg>
      </button>
      {menuPos && (
        <ul className="NavStatusDropdown_menu" style={{ position: 'fixed', ...menuPos }}>
          {allOptions.map(opt => (
            <li key={opt.key}>
              <button
                disabled={!selectableKeys.includes(opt.key) || opt.disabled}
                onClick={() => {
                  opt.handler()
                  setMenuPos(null)
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** 共通: 全タブで使用（Nav・レイアウト） */
interface IPostEditBaseProps {
  post: any
  isNew: boolean
  user: IUser
}

/** info タブ用 */
interface IPostEditInfoProps {
  categories: any
  tags: any
  languages: any
  areas: any
  cancel_policies: any
  display_languages: any
  is_agent: boolean
}

/** detail タブ用 */
interface IPostEditDetailProps {
  cancel_policy: string
}

/** guide_fee / job_bulk_plan タブ用 */
interface IPostEditGuideFeeProps {
  agent_ranks: any
}

/** settings タブ用 */
interface IPostEditSettingsProps {
  sound_out_templates: any
  formal_request_templates: any
  tour_memo_templates: any
  tour_skills: any
  show_other_groups: any
  selectable_posts: any
}

/** job_bulk_plan タブ用 */
interface IPostEditJobBulkPlanProps {
  job_bulk_plans: any
  start_times: string[]
  tour_skills: any
  skills: any
}

type IPostEditProps = IPostEditBaseProps &
  Partial<IPostEditInfoProps> &
  Partial<IPostEditDetailProps> &
  Partial<IPostEditGuideFeeProps> &
  Partial<IPostEditSettingsProps> &
  Partial<IPostEditJobBulkPlanProps>

const getInitialPage = () => {
  if (typeof window === 'undefined') {
    return 'info'
  }
  const urlParams = new URLSearchParams(window.location.search)
  return urlParams.get('active') || 'info'
}

const PostNew: React.FC<IPostEditProps> = props => {
  const { post: parsedPost } = postService.getPostFromJson(props.post)
  const { data: displayLanguages } = utilService.getDataFromJson(
    props.display_languages || { data: [] }
  ) as { data: IDisplayLanguage[] }

  Hooks.SetDocumentTitle(parsedPost.name ? parsedPost.name : I18n.t('post.create_post'))
  const currentImages = parsedPost.post_images || []
  const initialImages = currentImages.map((image: any) => ({
    id: image.id,
    url: image.image_url,
    description: image.description,
  }))
  const [images, setImages] = React.useState(initialImages)
  const [startTimes, setStartTimes] = React.useState(parsedPost.post_start_times || [])
  const [page, setPage] = React.useState(getInitialPage)
  const [post, setPost] = React.useState(parsedPost || {})
  // 公開に必要な支払設定の未設定項目(編集画面のシリアライズ時のみ付与される)
  const missingPaymentSettings: string[] = parsedPost?.missing_payment_settings || []
  const [isNew, setIsNew] = React.useState(props.isNew)
  const { data: categories } = postService.getDataFromJson(props.categories || { data: [] }) as {
    data: ICategory[]
  }
  const { data: tags } = postService.getDataFromJson(props.tags || { data: [] }) as { data: ITag[] }
  const { data: languages } = postService.getDataFromJson(props.languages || { data: [] }) as {
    data: ILanguage[]
  }
  const { data: areas } = postService.getDataFromJson(props.areas || { data: [] }) as {
    data: IArea[]
  }
  const { data: agentRanks } = postService.getDataFromJson(props.agent_ranks || { data: [] }) as {
    data: IAgentRank[]
  }
  const { data: cancelPolicies } = postService.getDataFromJson(
    props.cancel_policies || { data: [] }
  ) as { data: ICancelPolicy[] }
  const { data: soundoutTemplates } = postService.getDataFromJson(
    props.sound_out_templates || { data: [] }
  ) as { data: ISoundOutTemplate[] }
  const { data: formalRequestTemplates } = postService.getDataFromJson(
    props.formal_request_templates || { data: [] }
  ) as { data: IFormalRequestTemplate[] }
  const { data: tourSkills } = postService.getDataFromJson(props.tour_skills || { data: [] }) as {
    data: ITourSkill[]
  }
  const { data: tourMemoTemplates } = postService.getDataFromJson(
    props.tour_memo_templates || { data: [] }
  ) as { data: ITourMemoTemplate[] }
  const { data: jobBulkPlans, pagination: jobBulkPlansPagination } = postService.getDataFromJson(
    props.job_bulk_plans?.job_bulk_plans || { data: [] }
  ) as { data: IJobBulkPlan[]; pagination: any }
  const { data: skills } = postService.getDataFromJson(props.skills || { data: [] }) as {
    data: ISkill[]
  }

  const changePage = changedPage => {
    const basePath = location.pathname.replace(/\/edit$/, '')
    location.href = `${basePath}/edit?active=${changedPage}`
  }

  const changePageFromParam = () => {
    const urlParams = new URLSearchParams(window.location.search)
    const active = urlParams.get('active')
    if (active) {
      setPage(active)
    }
  }

  React.useEffect(() => {
    changePageFromParam()
    window.onpopstate = () => {
      changePageFromParam()
    }
  }, [])

  const runPublish = async (limitedAccess: boolean) => {
    try {
      const { flush } = await (limitedAccess
        ? postService.limitedPublish(post)
        : postService.publish(post))
      setPost({ ...post, aasm_state: 'published', limited_access: limitedAccess })
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } catch (error) {
      console.error('Error publishing post:', error)
    }
  }
  const publishPost = () => runPublish(false)
  const limitedPublishPost = () => runPublish(true)

  const suspendPost = async () => {
    try {
      const { flush } = await postService.suspend(post)
      setPost({ ...post, aasm_state: 'suspended' })
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } catch (error) {
      console.error('Error suspending post:', error)
    }
  }

  const closePost = async () => {
    try {
      const { flush } = await postService.closePost(post.id)
      setPost({ ...post, aasm_state: 'closed' })
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } catch (error) {
      console.error('Error closing post:', error)
    }
  }

  const copyPostUrl = () => {
    const url = `${location.origin}/posts/${parsedPost.slug}`
    navigator.clipboard.writeText(url).then(() => {
      window.flashMessages.addMessage({ text: I18n.t('generic.copied'), type: 'success' })
    })
  }

  const getInputStatus = currentPage => {
    const status = { valid: false, message: '' }

    switch (currentPage) {
      case 'photo':
        ;(status.valid = images.length > 0),
          (status.message = images.length
            ? I18n.t('post.image_has_uploaded')
            : I18n.t('post.please_upload_image'))
        break
      case 'address':
        status.valid = true
        break
      case 'startTime':
        status.valid = startTimes.length > 0
        break
    }

    return status
  }

  return (
    <SidebarLayout user={props.user}>
      <PostNewWrapper>
        <Nav>
          <ul className="Nav_List">
            {!props.isNew && (
              <li
                className={ClassNames({ active: page === 'job_bulk_plan' })}
                onClick={() => changePage('job_bulk_plan')}
              >
                {I18n.t('generic.job_bulk_plan')}
              </li>
            )}
            <li
              className={ClassNames({ active: page === 'info' })}
              onClick={() => changePage('info')}
            >
              {I18n.t('post.basic_info')}
            </li>
            <li
              className={ClassNames({ active: page === 'detail', disabled: props.isNew })}
              onClick={() => {
                if (!props.isNew) {
                  changePage('detail')
                }
              }}
            >
              {I18n.t('post.detail_info')}
            </li>
            {!post.family_owner && (
              <li
                className={ClassNames({ active: page === 'price', disabled: props.isNew })}
                onClick={() => {
                  if (!props.isNew) {
                    changePage('price')
                  }
                }}
              >
                {I18n.t('generic.price')}
                {post.post_type === 'private' ? '' : ' & Ages'}
              </li>
            )}
            {props?.user?.agent && !post.family_owner && (
              <li
                className={ClassNames({ active: page === 'guide_fee', disabled: props.isNew })}
                onClick={() => {
                  if (!props.isNew) {
                    changePage('guide_fee')
                  }
                }}
              >
                <span>{I18n.t('generic.guide_fee')}</span>
                {missingPaymentSettings.includes('guide_fee') && (
                  <span className="note">{I18n.t('post.please_input_guide_fee')}</span>
                )}
              </li>
            )}
            <li
              className={ClassNames({ active: page === 'address', disabled: props.isNew })}
              onClick={() => {
                if (!props.isNew) {
                  changePage('address')
                }
              }}
            >
              <span>{I18n.t('generic.meeting_point')}</span>
              {!getInputStatus('address').valid && (
                <span className="note">{I18n.t('post.please_input_address')} </span>
              )}
            </li>
            {!post.family_owner && (
              <li
                className={ClassNames({
                  active: page === 'startTime',
                  disabled: props.isNew || post.bokun_activity_id,
                })}
                onClick={() => {
                  if (props.isNew || post.bokun_activity_id) {
                    return
                  }
                  changePage('startTime')
                }}
              >
                <span>{I18n.t('generic.start_time')}</span>
                {!post.bokun_activity_id && !getInputStatus('startTime')?.valid && (
                  <span className="note">{I18n.t('post.please_input_start_time')} </span>
                )}
              </li>
            )}
            <li
              className={ClassNames({
                active: page === 'photo',
                disabled: props.isNew,
              })}
              onClick={() => {
                if (!props.isNew) {
                  changePage('photo')
                }
              }}
            >
              <span>{I18n.t('generic.photo')}</span>
              {!getInputStatus('photo').valid && (
                <span className="note">{I18n.t('post.please_upload_image')}</span>
              )}
            </li>
            <li
              className={ClassNames({
                active: page === 'video',
                disabled: props.isNew,
              })}
              onClick={() => {
                if (!props.isNew) {
                  changePage('video')
                }
              }}
            >
              <span>{I18n.t('generic.video')}</span>
            </li>
            {!post.family_owner && (
              <li
                className={ClassNames({
                  active: page === 'calendar',
                  disabled: props.isNew,
                })}
                onClick={() => {
                  if (!props.isNew) {
                    changePage('calendar')
                  }
                }}
              >
                {I18n.t('generic.calendar')}
              </li>
            )}
            {!post.family_owner && (
              <>
                <li
                  className={ClassNames({
                    active: page === 'remindMailTemplate',
                    disabled: props.isNew,
                  })}
                  onClick={() => {
                    if (!props.isNew) {
                      changePage('remindMailTemplate')
                    }
                  }}
                >
                  {I18n.t('activerecord.models.remind_mail_template')}
                </li>
                <li
                  className={ClassNames({ active: page === 'cost', disabled: props.isNew })}
                  onClick={() => {
                    if (!props.isNew) {
                      changePage('cost')
                    }
                  }}
                >
                  <span>{I18n.t('activerecord.models.post_cost')}</span>
                  {missingPaymentSettings.includes('post_cost') && (
                    <span className="note">{I18n.t('post.please_input_cost')}</span>
                  )}
                </li>
              </>
            )}
            <li
              className={ClassNames({ active: page === 'settings', disabled: props.isNew })}
              onClick={() => {
                if (!props.isNew) {
                  changePage('settings')
                }
              }}
            >
              {I18n.t('generic.setting')}
            </li>

            {props?.user?.agent && (
              <li>
                <NavStatusDropdown
                  post={post}
                  onPublish={publishPost}
                  onLimitedPublish={limitedPublishPost}
                  onSuspend={suspendPost}
                  onClose={closePost}
                  publishDisabled={
                    !getInputStatus('photo').valid ||
                    !getInputStatus('address').valid ||
                    missingPaymentSettings.length > 0
                  }
                />
              </li>
            )}

            <li>
              {!props.isNew && <Button handleClick={copyPostUrl}>{I18n.t('post.copy_url')}</Button>}
            </li>
          </ul>
        </Nav>
        <Editor>
          <div className={`Editor_Panel ${page}`}>
            {page === 'job_bulk_plan' && (
              <JobBulkPlanList
                post={parsedPost}
                jobBulkPlans={jobBulkPlans || []}
                startTimes={props.start_times}
                tour_skills={tourSkills || []}
                skills={skills || []}
                agent_ranks={agentRanks || []}
                pagination={jobBulkPlansPagination}
              />
            )}
            {page === 'info' && (
              <PostBasicInfo
                post={parsedPost}
                categories={categories}
                tags={tags}
                languages={languages}
                areas={areas}
                cancel_policies={cancelPolicies}
                slug={parsedPost?.slug}
                isNew={isNew}
                setIsNew={setIsNew}
                user={props?.user}
                display_languages={displayLanguages}
                is_agent={props?.is_agent}
              />
            )}
            {page === 'detail' && (
              <PostDetailInfo
                post={parsedPost}
                cancelPolicy={props.cancel_policy}
                user={props?.user}
              />
            )}

            {page === 'price' &&
              (post.post_type === 'private' ? (
                <PostPrivateTourPrice post={parsedPost} />
              ) : (
                <PostGroupTourPrice post={parsedPost} />
              ))}
            {page === 'guide_fee' && props?.user?.agent && (
              <PostGuideRankFee post={parsedPost} agent_ranks={agentRanks} />
            )}
            {page === 'address' && (
              <PostAddress post={parsedPost} meetingPoints={parsedPost.meeting_points} />
            )}
            {page === 'photo' && (
              <>
                <h2 className="Editor_Title">{I18n.t('generic.photo')}</h2>
                <div className="Editor_Main">
                  <PostImages post={parsedPost} images={initialImages} updateImages={setImages} />
                </div>
              </>
            )}
            {page === 'video' && (
              <>
                <h2 className="Editor_Title">{I18n.t('generic.video')}</h2>
                <div className="Editor_Main">
                  <PostVideos post={parsedPost} postVideos={parsedPost.post_videos} />
                </div>
              </>
            )}
            {page === 'startTime' && (
              <PostStartTime
                post={parsedPost}
                startTimes={startTimes}
                setStartTimes={setStartTimes}
              />
            )}
            {page === 'calendar' && <Calendar post={parsedPost} agent={props?.user?.agent} />}
            {page === 'remindMailTemplate' && (
              <PostRemindMailTemplate
                post_id={parsedPost.id}
                mail_template={parsedPost.remind_mail_template}
                user={parsedPost.user ?? props.user}
              />
            )}
            {page === 'cost' && <PostCost post={parsedPost} />}
            {page === 'settings' && (
              <PostSettings
                post={parsedPost}
                tour_skills={tourSkills || []}
                sound_out_templates={soundoutTemplates || []}
                formal_request_templates={formalRequestTemplates || []}
                tour_memo_templates={tourMemoTemplates || []}
                agent_ranks={agentRanks || []}
                show_other_groups={props.show_other_groups || []}
                selectable_posts={props.selectable_posts || []}
              />
            )}
          </div>
        </Editor>
      </PostNewWrapper>
    </SidebarLayout>
  )
}

const PostNewWrapper = styled.div`
  display: flex;
  //margin-top: ${constants.HEADER_HEIGHT}px;
  height: calc(100vh - ${constants.HEADER_HEIGHT}px);

  //@media (max-width: ${constants.BREAKPOINT_TABLET_LARGE}px) {
    flex-direction: column;
  //}

  .PostNew_Map {
    height: 240px;
    margin-top: 24px;

    &.disabled {
      display: none;
    }
  }
`

const Nav = styled.section`
  border-bottom: solid 1px ${constants.COLORS.divider};
  box-shadow: 0 1px 3px 0 rgba(21, 27, 38, 0.1);
  z-index: 1000;
  width: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  white-space: nowrap;
  border: none;
  padding-bottom: 17px;
  user-select: none;
  .Nav_List {
    padding: 16px 0;
    height: inherit;
    padding: 0;
    display: block;
    line-height: 40px;

    li {
      display: inline-block;
      font-size: 14px;
      padding: 0 16px;
      cursor: pointer;

      .note {
        margin-left: 6px;
        color: ${constants.COLORS.subText};
        font-size: 12px;
        font-weight: normal;
      }

      > .Button {
        width: 100%;
        text-align: center;
        height: 32px;
        padding: 0 12px;

        a {
          justify-content: center;
        }
      }

      &.active {
        color: ${constants.COLORS.primary};
        font-weight: bold;
        border-top: solid 2px ${constants.COLORS.primary};
      }

      &.disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      &:hover:not(.active):not(.disabled) {
        color: ${constants.COLORS.primary};
      }

      &:last-child {
        padding-left: 8px;
      }
    }
  }

  .Nav_Buttons {
    padding: 8px 20px;
  }

  .NavStatusDropdown {
    position: relative;
    display: inline-block;

    .NavStatusDropdown_btn {
      display: inline-flex;
      align-items: center;
      padding: 0;
      border-radius: 4px;
      border: none;
      cursor: pointer;
      font-size: 13px;
      font-weight: bold;
      color: ${constants.COLORS.subText};
      background: ${constants.COLORS.lightGray};
      span {
        display: inline-block;
        min-width: 100px;
        padding: 4px 16px;
      }
      .NavStatusDropdown_icon {
        width: 32px;
        height: 32px;
        padding: 4px;
        border-left: 1px solid ${constants.COLORS.white};
      }
    }
    &.published .NavStatusDropdown_btn {
      color: ${constants.COLORS.white};
      background: ${constants.COLORS.primary};
    }
    &.limited_published .NavStatusDropdown_btn {
      color: ${constants.COLORS.white};
      background: ${constants.COLORS.info2};
    }
    &.suspended .NavStatusDropdown_btn {
      color: ${constants.COLORS.subText};
      background: ${constants.COLORS.disabled};
    }
    &.closed .NavStatusDropdown_btn {
      color: ${constants.COLORS.white};
      background: ${constants.COLORS.subText};
    }

    .NavStatusDropdown_menu {
      z-index: 9999;
      background: ${constants.COLORS.white};
      border: 1px solid ${constants.COLORS.divider};
      border-radius: 8px;
      box-shadow: ${constants.COLORS.cardShadow};
      min-width: 150px;
      padding: 0;
      margin: 0;
      list-style: none;
      overflow: hidden;

      li {
        display: block;
        margin: 0;
        padding: 0;
        border-bottom: 1px solid ${constants.COLORS.divider2};
        min-width: 160px;
        &:last-child {
          border-bottom: none;
        }
        button {
          display: block;
          width: 100%;
          padding: 8px 16px;
          line-height: 24px;
          margin: 0;
          background: none;
          border: none;
          text-align: left;
          cursor: pointer;
          font-size: 14px;
          &:disabled {
            color: ${constants.COLORS.disabled};
            cursor: default;
          }
          &:not(:disabled):hover {
            background: ${constants.COLORS.background};
          }
        }
      }
    }
  }
`

const Editor = styled.section`
  flex: 1;
  padding: 16px;
  height: 100%;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  background-color: ${constants.COLORS.white};

  .Editor_Panel {
    background-color: ${constants.COLORS.white};
    border-radius: 4px;
    box-shadow: 0 1px 3px 0 rgba(21, 27, 38, 0.15);
    max-width: 880px;
    margin: 0 auto;

    &.calendar {
      max-width: 1200px;
      height: 100%;
    }
  }

  .Editor_Title {
    font-size: 18px;
    border-bottom: solid 1px #eaedef;
    padding: 16px 20px 8px;
  }

  .Editor_Main,
  .Form {
    padding: 20px;
  }

  .Editor_Footer {
    display: flex;
    justify-content: center;
    margin-top: 24px;
    position: sticky;
    bottom: -16px;
    padding: 8px 0;
    background-color: ${constants.COLORS.white};
  }
`

interface IInputProps {
  text?: boolean
  radio?: boolean
  checkbox?: boolean
}

export default PostNew
